import gsap from 'gsap';
import { Texture, Text as PixiText } from 'pixi.js';
import { observer } from 'mobx-react-lite';
import { Container, Sprite } from '@pixi/react';
import { useRef, useLayoutEffect, useState, useEffect, useMemo, useCallback } from 'react';
import { PgWheelThemeConfigs } from '../../../../../../constants/pg-wheel-theme-configs';
import { isoStandard, PixiAssets, boostedJackpotDateHelper } from '../../../../../../helpers';
import { soundService, SoundTypes } from '../../../../../../../services/soundService';
import { useStore, useTranslation } from '../../../../../../../hooks';
import { BoostedJackpotWheelProps } from '../../../index.types';
import { PGWheelRewardType } from '../../../../../../../enums';
import { isMobile } from '../../../../../../../helpers';
import { T } from '../../../../../../../constants';
import { Spine } from '../../../../../../components';

let wheelAnimation: any = null;
let getBoostedJackpotTimeOutId: any | NodeJS.Timeout = null;
let wheelSettleAnimation: gsap.core.Tween | gsap.core.Timeline | null = null;
let wheelIntroAnimation: gsap.core.Timeline | null = null;
let wheelEndAnimation: gsap.core.Timeline | null = null;
export const BoostedJackpotWheel: React.FC<BoostedJackpotWheelProps> = observer(({
  sectors = 6,
  wheelValues,
  timeSlotRef,
  buttonSlotRef,
  bgSpineRef,
}) => {
  const wheelRef: any = useRef(null);
  const wheelUnderlayRef: any = useRef(null);
  const canvasRef: any = useRef(null);
  const wheelThemeFrameRef: any = useRef(null);
  const wheelThemeArrowRef: any = useRef(null);
  const wheelThemeLogoRef: any = useRef(null);
  const wheelSpinRef: any = useRef(null);
  const wheelSpinLabelRef = useRef<any>(null);
  const wheelGroupRef: any = useRef(null);
  const wheelShakeTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const [texture, setTexture] = useState<Texture | any>(null);
  const [winOverlayTexture, setWinOverlayTexture] = useState<Texture | any>(null);
  const [showWinOverlay, setShowWinOverlay] = useState<boolean>(false);
  const [showWinHighlight, setShowWinHighlight] = useState<boolean>(true);
  const [wheelIntroSyncKey, setWheelIntroSyncKey] = useState<number>(0);
  const winOverlayTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const winPopupTimeoutRef = useRef<number | null>(null);
  const wheelEndTimeoutRef = useRef<number | null>(null);
  const [spinClick, setSpinClick] = useState<boolean>(true);
  const wheelTimerFiredRef = useRef(false);
  const [wheelTimerTick, setWheelTimerTick] = useState(0);

  const t = useTranslation();
  const { jackpotStore, boostedJackpotStore, gameStore } = useStore();
  const { replaySpin, setReplaySpin, isWheelZoomOut, sectorStopIndex, spinWheelWinAmount, spinWheelWinIsFreeBet, setIsBoostedJackpotWinPopup, setIsWheel, setSectorStopIndex, setIsWheelZoomOut } = boostedJackpotStore;
  const { jackPotExpireTime } = jackpotStore;
  const partnerId = gameStore.partnerId;
  const { currencySymbol } = gameStore;
  const radius = 300;
  const sectorColors = PgWheelThemeConfigs[partnerId].sectorColors;
  const sectorTextColors = PgWheelThemeConfigs[partnerId].sectorTextColors;
  const sectorAlpha = PgWheelThemeConfigs[partnerId].sectorAlpha;
  const brandHighlightColor = PgWheelThemeConfigs[partnerId].sectorTextColors?.[0] || '#ffcd6a';
  const sectorTexturePath = 'frame/frameModuleResources/boostedJackpot/wheelThemes/sector/sector-texture.png';
  const sectorRadius = radius - 20;
  const sectorUnderlayTexture = useMemo(() => {
    if (PixiAssets.cache.has('boostedWheelSectorTexture')) {
      return PixiAssets.get('boostedWheelSectorTexture') as Texture;
    }

    return Texture.from(sectorTexturePath);
  }, [sectorTexturePath]);
  const sectorBorderColor = '#8c7650';

  const playStartThenIdle = (spine: Spine, fallbackIdle = 'loop') => {
    if (spine.state?.hasAnimation('start')) {
      spine.play('start', false);
      spine.addListener(
        'complete',
        () => {
          if (spine.state?.hasAnimation('idle')) {
            spine.play('idle', true);
            return;
          }

          if (spine.state?.hasAnimation(fallbackIdle)) {
            spine.play(fallbackIdle, true);
          }
        },
        null,
        '',
        true
      );
      return;
    }

    if (spine.state?.hasAnimation('idle')) {
      spine.play('idle', true);
      return;
    }

    if (spine.state?.hasAnimation(fallbackIdle)) {
      spine.play(fallbackIdle, true);
    }
  };

  const playEndIfExists = (containerRef: any) => {
    const spineInstance = containerRef?.current?.children?.[0];
    if (spineInstance?.state?.hasAnimation?.('end')) {
      spineInstance.play('end', false);
    }
  };

  const getEndDurationMs = (containerRef: any) => {
    const spineInstance = containerRef?.current?.children?.[0];
    if (!spineInstance?.state?.hasAnimation?.('end')) return 0;

    const durationSeconds = spineInstance.getAnimationDuration?.('end') || 0;
    return Math.ceil(durationSeconds * 1000);
  };

  const getStartDurationMs = (containerRef: any) => {
    const spineInstance = containerRef?.current?.children?.[0];
    if (!spineInstance?.state?.hasAnimation?.('start')) return 0;

    const durationSeconds = spineInstance.getAnimationDuration?.('start') || 0;
    return Math.ceil(durationSeconds * 1000);
  };

  const playHideIfExists = useCallback((containerRef: any) => {
    const spineInstance = containerRef?.current?.children?.[0];
    if (!spineInstance?.state?.hasAnimation?.('hide')) return;

    spineInstance.play('hide', false);

    if (containerRef === buttonSlotRef && wheelSpinLabelRef.current) {
      const hideDuration = spineInstance.getAnimationDuration?.('hide') || 0.3;
      gsap.to(wheelSpinLabelRef.current, { alpha: 0, duration: hideDuration, ease: 'linear' });
    }
  }, [buttonSlotRef]);

  useLayoutEffect(() => {
    const createCanvas: any | HTMLCanvasElement = document.createElement('canvas');
    createCanvas.width = 570;
    createCanvas.height = 570;
    canvasRef.current = createCanvas;
    const canvas: any = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const center: { x: number, y: number } = { x: canvas.width / 2, y: canvas.height / 2 };

    const drawWheel = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const sectorAngle: number = (2 * Math.PI) / sectors;
      // Sort a copy by position so position 1 is drawn first (index 0)
      const sorted = [...wheelValues].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      const allSectorTexts = sorted.map((s) =>
        s?.rewardType === PGWheelRewardType.FreeBet
          ? `${isoStandard(s.amount * s.freeBetCount) || ''} FS`
          : isoStandard(s.amount)
      );
      const maxSectorLen = Math.max(...allSectorTexts.map((s) => s.length), 0);
      const uniformFontSize = sectors <= 12 && maxSectorLen <= 7 ? 36 : 21;

      for (let i = 0; i < sectors; i++) {
        const startAngle: number = i * sectorAngle;
        const endAngle: number = (i + 1) * sectorAngle;

        ctx.beginPath();
        ctx.moveTo(center.x, center.y);
        ctx.arc(center.x, center.y, sectorRadius, startAngle, endAngle);
        ctx.save();
        ctx.globalAlpha = sectorAlpha;
        ctx.fillStyle = sectorColors[i % 2];
        ctx.fill();
        ctx.restore();
        ctx.closePath();

        ctx.strokeStyle = sectorBorderColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(
          center.x + Math.cos(startAngle) * sectorRadius,
          center.y + Math.sin(startAngle) * sectorRadius
        );
        ctx.lineTo(center.x, center.y);
        ctx.stroke();
        ctx.strokeStyle = sectorBorderColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(
          center.x + Math.cos(endAngle) * sectorRadius,
          center.y + Math.sin(endAngle) * sectorRadius
        );
        ctx.lineTo(center.x, center.y);
        ctx.stroke();
        ctx.save();
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'right';
        ctx.translate(center.x, center.y);
        ctx.rotate((startAngle + endAngle) / 2);
        ctx.fillStyle = sectorTextColors[i % 2];
        const sectorText = allSectorTexts[i];
        ctx.font = `bold ${uniformFontSize}px Verdana`;
        ctx.fillText(sectorText, 250, 1);
        ctx.restore();
      };
    };

    const updateWheelTexture = () => {
      const dataURL = canvas.toDataURL('image/png');
      return setTexture(Texture.from(dataURL));
    };

    drawWheel();
    updateWheelTexture();
  }, [sectorAlpha, sectorBorderColor, sectorColors, sectorTextColors, sectors, wheelValues]);

  useEffect(() => {
    const wheelSprite = wheelRef.current;
    const wheelScale = wheelSprite?.scale;
    const underlaySprite = wheelUnderlayRef.current;
    const underlayScale = underlaySprite?.scale;

    if (wheelSprite && wheelScale && texture) {
      const sectorAngle: number = 360 / sectors;
      // Position-1 sector (index 0 after sort) must start at 12 o'clock (270° canvas = top)
      const targetAngle: number = 360 * 5 + 270 - sectorAngle / 2;
      const frameStartDurationMs = getStartDurationMs(wheelThemeFrameRef);
      const introDurationSeconds = Math.max(frameStartDurationMs / 1000, 0.28);
      wheelIntroAnimation?.kill();
      wheelSettleAnimation?.kill();
      wheelSprite.alpha = 0;
      wheelScale.set(0, 0);
      if (underlaySprite && underlayScale) {
        underlaySprite.alpha = 0;
        underlayScale.set(0, 0);
      }
      wheelSettleAnimation = gsap.to(wheelSprite, {
        rotation: targetAngle * (Math.PI / 180),
        duration: 0,
        ease: 'linear',
      });
      wheelIntroAnimation = gsap.timeline()
        .to(underlaySprite, {
          alpha: 1,
          duration: introDurationSeconds,
          ease: 'power1.out',
        }, 0)
        .to(underlayScale, {
          x: 1,
          y: 1,
          duration: introDurationSeconds,
          ease: 'power2.out',
        }, 0)
        .to(wheelSprite, {
          alpha: 1,
          duration: introDurationSeconds,
          ease: 'power1.out',
        }, 0)
        .to(wheelScale, {
          x: 1,
          y: 1,
          duration: introDurationSeconds,
          ease: 'power2.out',
        }, 0);
    };

    return () => {
      wheelIntroAnimation?.kill();
      wheelIntroAnimation = null;
      wheelSettleAnimation?.kill();
      wheelSettleAnimation = null;
      if (wheelSprite) gsap.killTweensOf(wheelSprite);
      if (wheelScale) gsap.killTweensOf(wheelScale);
      if (underlaySprite) gsap.killTweensOf(underlaySprite);
      if (underlayScale) gsap.killTweensOf(underlayScale);
    };
  }, [wheelIntroSyncKey, wheelRef, texture, sectors]);

  const playLoopIfExists = useCallback((containerRef: any) => {
    const spine = containerRef?.current?.children?.[0];
    if (spine?.state?.hasAnimation?.('loop')) {
      spine.play('loop', true);
    }
  }, []);

  const startWheelSpin = useCallback(() => {
    if (!wheelRef.current) return;
    soundService.play(SoundTypes.WheelSpinning);
    wheelAnimation?.kill();
    wheelAnimation = null;

    playLoopIfExists(wheelThemeFrameRef);
    playLoopIfExists(wheelThemeArrowRef);
    playLoopIfExists(wheelThemeLogoRef);

    // Continuous forward spin until server result (sectorStopIndex) arrives.
    wheelAnimation = gsap.fromTo(
      wheelRef.current,
      { rotation: wheelRef.current.rotation },
      {
        rotation: wheelRef.current.rotation + Math.PI * 2,
        duration: 0.8,
        repeat: -1,
        ease: 'none',
      }
    );
  }, [playLoopIfExists]);

  const mountWheelThemeFrame = useCallback(() => {
    if (!wheelThemeFrameRef.current) return;

    const wheelThemeFrame: Spine = new Spine({ key: 'boostedWheelThemeFrame' });
    playStartThenIdle(wheelThemeFrame, 'loop');
    wheelThemeFrameRef.current.addChild(wheelThemeFrame);
    setWheelIntroSyncKey((prev) => prev + 1);
  }, []);

  const mountWheelThemeArrow = useCallback(() => {
    if (!wheelThemeArrowRef.current) return;

    const wheelThemeArrow: Spine = new Spine({ key: 'boostedWheelThemeArrow' });
    playStartThenIdle(wheelThemeArrow, 'loop');
    wheelThemeArrowRef.current.addChild(wheelThemeArrow);
  }, []);

  const mountWheelThemeLogo = useCallback(() => {
    if (!wheelThemeLogoRef.current) return;

    const wheelThemeLogo: Spine = new Spine({ key: 'boostedWheelThemeLogo' });
    playStartThenIdle(wheelThemeLogo, 'loop');
    wheelThemeLogo.scale.set(0.9);
    wheelThemeLogoRef.current.addChild(wheelThemeLogo);
  }, []);

  const spinButtonText = t(T.SPIN).toUpperCase();

  const mountWheelSpinButton = useCallback(() => {
    if (!buttonSlotRef?.current) return;

    if (isMobile()) buttonSlotRef.current.scale.set(0.9);

    const wheelThemeButton: Spine = new Spine({ key: 'boostedWheelThemeButton' });
    playStartThenIdle(wheelThemeButton, 'default');
    buttonSlotRef.current.addChild(wheelThemeButton);

    const buttonTextColor = PgWheelThemeConfigs[partnerId].buttonTextColor;
    const buttonShadowColor = PgWheelThemeConfigs[partnerId].buttonShadowColor;
    const buttonDropShadow = PgWheelThemeConfigs[partnerId].buttonDropShadow;
    const spinLabel = new PixiText(spinButtonText, {
      fontSize: 34,
      align: 'center',
      fill: buttonTextColor,
      stroke: buttonTextColor,
      strokeThickness: 3,
      letterSpacing: 1.2,
      dropShadow: buttonDropShadow,
      dropShadowColor: buttonShadowColor,
      dropShadowBlur: 0,
      dropShadowDistance: 4.34,
      dropShadowAngle: Math.PI / 2,
      fontFamily: 'Pascal-Font',
    } as any);

    spinLabel.anchor.set(0.5);
    wheelSpinLabelRef.current = spinLabel;
    buttonSlotRef.current.addChild(spinLabel);
  }, [partnerId, buttonSlotRef, spinButtonText]);

  const scheduleAutoSpin = useCallback(() => {
    const expireDate = boostedJackpotStore.wheelTriggerExpireDate || boostedJackpotStore.wheelInfo?.wheelExpireDate;
    const delay = expireDate ? Math.max(0, new Date(expireDate).getTime() - Date.now()) : 300000;
    getBoostedJackpotTimeOutId = setTimeout(() => {
      clearTimeout(getBoostedJackpotTimeOutId);
      playHideIfExists(buttonSlotRef);
      setSpinClick(false);
      startWheelSpin();
      void boostedJackpotStore.getBoostedJackpot();
    }, delay);
  }, [startWheelSpin, playHideIfExists, buttonSlotRef]);

  useEffect(() => {
    scheduleAutoSpin();
    mountWheelThemeFrame();
    mountWheelThemeArrow();
    mountWheelThemeLogo();
    mountWheelSpinButton();

    return () => {
      clearTimeout(getBoostedJackpotTimeOutId);
      wheelAnimation?.kill();
      wheelAnimation = null;
      wheelIntroAnimation?.kill();
      wheelIntroAnimation = null;
      wheelSettleAnimation?.kill();
      wheelSettleAnimation = null;
      wheelEndAnimation?.kill();
      wheelEndAnimation = null;
      winOverlayTimelineRef.current?.kill();
      winOverlayTimelineRef.current = null;
      if (winPopupTimeoutRef.current) {
        window.clearTimeout(winPopupTimeoutRef.current);
        winPopupTimeoutRef.current = null;
      }
      if (wheelEndTimeoutRef.current) {
        window.clearTimeout(wheelEndTimeoutRef.current);
        wheelEndTimeoutRef.current = null;
      }
      wheelShakeTimelineRef.current?.kill();
      wheelShakeTimelineRef.current = null;
      if (wheelRef.current) {
        gsap.killTweensOf(wheelRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const wheelSprite = wheelRef.current;

    if (wheelAnimation && sectorStopIndex !== null) {
      const currentRotation = wheelSprite.rotation;
      wheelAnimation.kill();
      if (!wheelSprite) return;

      const TWO_PI = Math.PI * 2;
      const sectorAngle: number = 360 / sectors;
      // The desired final resting angle in radians
      const finalDegrees = 270 - (sectorStopIndex * sectorAngle + sectorAngle / 2);
      const finalRadBase = finalDegrees * (Math.PI / 180);
      // Normalize both angles to [0, 2π)
      const finalNorm = ((finalRadBase % TWO_PI) + TWO_PI) % TWO_PI;
      const currentNorm = ((currentRotation % TWO_PI) + TWO_PI) % TWO_PI;
      // Always rotate forward to target
      let forwardDelta = finalNorm - currentNorm;
      if (forwardDelta <= 0) forwardDelta += TWO_PI;
      // Add 3 extra full rotations so the wheel spins visibly before stopping
      const targetRotation = currentRotation + forwardDelta + 3 * TWO_PI;
      // Overshoot ~5 degrees past the sector centre, then settle back
      const overshootRad = 5 * (Math.PI / 180);

      wheelSettleAnimation?.kill();
      wheelSettleAnimation = gsap.timeline({
        onComplete: () => {
          // Play 'win' animation on all spine decorations
          const playWinIfExists = (containerRef: any) => {
            const spine = containerRef?.current?.children?.[0];
            if (spine?.state?.hasAnimation?.('win')) {
              spine.play('win', false);
            }
          };
          playWinIfExists(wheelThemeFrameRef);
          playWinIfExists(wheelThemeArrowRef);
          playWinIfExists(wheelThemeLogoRef);

          // Shake the whole wheel vertically
          const group = wheelGroupRef.current;
          if (group) {
            wheelShakeTimelineRef.current?.kill();
            const originY = group.y;
            wheelShakeTimelineRef.current = gsap.timeline()
              .to(group, { y: originY - 18, duration: 0.07, ease: 'power1.out' })
              .to(group, { y: originY + 14, duration: 0.07, ease: 'power1.inOut' })
              .to(group, { y: originY - 10, duration: 0.06, ease: 'power1.out' })
              .to(group, { y: originY + 7, duration: 0.06, ease: 'power1.inOut' })
              .to(group, { y: originY - 4, duration: 0.05, ease: 'power1.out' })
              .to(group, { y: originY, duration: 0.05, ease: 'power1.inOut' });
          }

          if (Number(spinWheelWinAmount || 0) > 0) {
            setShowWinOverlay(true);
            setShowWinHighlight(true);
            winOverlayTimelineRef.current?.kill();

            winOverlayTimelineRef.current = gsap.timeline()
              .to({}, { duration: 0.2, onComplete: () => setShowWinHighlight(false) })
              .to({}, { duration: 0.2, onComplete: () => setShowWinHighlight(true) })
              .to({}, { duration: 0.2, onComplete: () => setShowWinHighlight(false) })
              .to({}, { duration: 0.2, onComplete: () => setShowWinHighlight(true) });

            if (winPopupTimeoutRef.current) {
              window.clearTimeout(winPopupTimeoutRef.current);
            }
            winPopupTimeoutRef.current = window.setTimeout(() => {
              setShowWinOverlay(false);
              setSectorStopIndex(null);
              // Trigger zoom-out; wrapper will set isBoostedJackpotWinPopup after zoom completes
              setIsWheelZoomOut(true);
            }, 900);

            const bgSpine = bgSpineRef?.current;
            const hasDisappear = bgSpine?.state?.hasAnimation?.('disappear');
            if (hasDisappear) {
              bgSpine.play('disappear', false);
            }
            return;
          }

          setSectorStopIndex(null);
          // Always go through the zoom-out close animation before showing the congrats popup,
          // so the wheel visually closes first regardless of the win amount.
          setIsWheelZoomOut(true);
        },
      })
        // Main deceleration: smooth power3 ease-out over 4.5 s, lands just past the target
        .to(wheelSprite, {
          rotation: targetRotation + overshootRad,
          duration: 4.5,
          ease: 'power3.out',
        })
        // Settle back to exact sector centre
        .to(wheelSprite, {
          rotation: targetRotation,
          duration: 0.35,
          ease: 'power1.inOut',
        });
    };
    return () => {
      wheelSettleAnimation?.kill();
      wheelSettleAnimation = null;
      if (wheelSprite) gsap.killTweensOf(wheelSprite);
    };
  }, [sectors, spinWheelWinAmount, sectorStopIndex]);

  useEffect(() => {
    if (!showWinOverlay) {
      setWinOverlayTexture(null);
      return;
    }

    const overlayCanvas = document.createElement('canvas');
    overlayCanvas.width = 570;
    overlayCanvas.height = 570;

    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;

    const centerX = overlayCanvas.width / 2;
    const centerY = overlayCanvas.height / 2;
    const overlayRadius = sectorRadius;
    const sectorAngleRad = (2 * Math.PI) / sectors;
    const middleAngleRad = -Math.PI / 2;
    const sectorStartAngle = middleAngleRad - sectorAngleRad / 2;
    const sectorEndAngle = middleAngleRad + sectorAngleRad / 2;

    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    ctx.beginPath();
    ctx.arc(centerX, centerY, overlayRadius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, overlayRadius, sectorStartAngle, sectorEndAngle);
    ctx.closePath();
    ctx.fillStyle = showWinHighlight
      ? brandHighlightColor
      : 'rgba(0, 0, 0, 0.55)';
    ctx.fill();

    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    ctx.translate(centerX, centerY);
    ctx.rotate(middleAngleRad);
    ctx.fillStyle = '#ffffff';
    const allSectorTextsForSize = wheelValues.map((s) =>
      s?.rewardType === PGWheelRewardType.FreeBet
        ? `${isoStandard(s.amount * s.freeBetCount) || ''} FS`
        : isoStandard(s.amount)
    );
    const maxSectorLen = Math.max(...allSectorTextsForSize.map((s) => s.length), 0);
    const winText = isoStandard(spinWheelWinAmount || 0);
    const winSuffix = spinWheelWinIsFreeBet ? ' FS' : '';
    const winLabel = `${winText}${winSuffix}`;
    const winFontSize = sectors <= 12 && maxSectorLen <= 7 ? 36 : 21;
    ctx.font = `bold ${winFontSize}px Verdana`;
    ctx.fillText(winLabel, 250, 1);
    ctx.restore();

    setWinOverlayTexture(Texture.from(overlayCanvas.toDataURL('image/png')));
  }, [brandHighlightColor, spinWheelWinAmount, spinWheelWinIsFreeBet, currencySymbol, sectors, sectorRadius, showWinHighlight, showWinOverlay]);

  const spinToStopSector = useCallback((e: any) => {
    if (!spinClick) return null;
    playHideIfExists(buttonSlotRef);
    soundService.play(SoundTypes.SpinWheelButton);
    setSpinClick(false);
    startWheelSpin();
    clearTimeout(getBoostedJackpotTimeOutId);
    void boostedJackpotStore.getBoostedJackpot();

    if (e) {
      if (e.target) e.target.interactive = false;
    };
  }, [spinClick, startWheelSpin, playHideIfExists, buttonSlotRef]);

  // Attach Pixi pointer events directly to buttonSlotRef.current.
  // The Container lives in the wrapper JSX (not here) so it is never removed from
  // _buttonSlot by React-Pixi's reconciler when the wheel component unmounts.
  useEffect(() => {
    const btn = buttonSlotRef?.current;
    if (!btn) return;

    btn.removeAllListeners?.();
    btn.interactive = spinClick;
    (btn as any).cursor = spinClick ? 'pointer' : 'default';

    btn.on('pointerover', () => { btn.children?.[0]?.play?.('hover', true); });
    btn.on('pointerout', () => { btn.children?.[0]?.play?.('default', true); });
    btn.on('pointerdown', () => { btn.children?.[0]?.play?.('pressed', true); });
    btn.on('pointerup', (e: any) => {
      spinToStopSector(e);
    });

    return () => {
      btn.removeAllListeners?.();
      btn.interactive = false;
    };
  }, [buttonSlotRef, spinClick, spinToStopSector, playHideIfExists]);

  useEffect(() => {
    if (jackPotExpireTime === 0) {
      spinToStopSector(null);
    };
  }, [jackPotExpireTime, spinToStopSector]);

  useEffect(() => {
    if (replaySpin) {
      setTimeout(() => {
        spinToStopSector(null);
      }, 2000)
      setReplaySpin(false)
    }
  }, [replaySpin, setReplaySpin, spinToStopSector])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setWheelTimerTick((prev) => prev + 1);
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
      wheelTimerFiredRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (wheelTimerFiredRef.current) return;
    const expireDate = boostedJackpotStore.wheelTriggerExpireDate || boostedJackpotStore.wheelInfo?.wheelExpireDate;
    if (!expireDate) return;
    const { days, hours, minutes, seconds } = boostedJackpotDateHelper(expireDate);

    if (days === '00' && hours === '00' && minutes === '00' && seconds === '00') {
      wheelTimerFiredRef.current = true;
      spinToStopSector(null);
    }
  }, [wheelTimerTick, boostedJackpotStore.wheelTriggerExpireDate, boostedJackpotStore.wheelInfo?.wheelExpireDate, spinToStopSector]);

  useEffect(() => {
    if (!isWheelZoomOut) return;

    const wheelSprite = wheelRef.current;
    const wheelScale = wheelSprite?.scale;
    const underlaySprite = wheelUnderlayRef.current;
    const underlayScale = underlaySprite?.scale;

    playEndIfExists(wheelThemeFrameRef);
    playEndIfExists(wheelThemeArrowRef);
    playEndIfExists(wheelThemeLogoRef);
    playEndIfExists(buttonSlotRef);
    playEndIfExists(timeSlotRef);

    const endAnimationDurationMs = Math.max(
      getEndDurationMs(wheelThemeFrameRef),
      getEndDurationMs(wheelThemeArrowRef),
      getEndDurationMs(wheelThemeLogoRef),
      getEndDurationMs(timeSlotRef),
      getEndDurationMs(buttonSlotRef),
      420,
    );

    if (wheelEndTimeoutRef.current) {
      window.clearTimeout(wheelEndTimeoutRef.current);
    }

    wheelEndAnimation?.kill();
    if (wheelSprite && wheelScale) {
      wheelEndAnimation = gsap.timeline()
        .to(underlayScale, {
          x: 0,
          y: 0,
          duration: endAnimationDurationMs / 1000,
          ease: 'power1.in',
        }, 0)
        .to(underlaySprite, {
          alpha: 0,
          duration: endAnimationDurationMs / 1000,
          ease: 'power1.in',
        }, 0)
        .to(wheelScale, {
          x: 0,
          y: 0,
          duration: endAnimationDurationMs / 1000,
          ease: 'power1.in',
        }, 0)
        .to(wheelSprite, {
          alpha: 0,
          duration: endAnimationDurationMs / 1000,
          ease: 'power1.in',
        }, 0);
    }

    wheelEndTimeoutRef.current = window.setTimeout(() => {
      wheelAnimation?.kill();
      wheelAnimation = null;
      wheelIntroAnimation?.kill();
      wheelIntroAnimation = null;
      wheelSettleAnimation?.kill();
      wheelSettleAnimation = null;
      wheelEndAnimation?.kill();
      wheelEndAnimation = null;
      if (wheelSprite) gsap.killTweensOf(wheelSprite);
      if (wheelScale) gsap.killTweensOf(wheelScale);
      if (underlaySprite) gsap.killTweensOf(underlaySprite);
      if (underlayScale) gsap.killTweensOf(underlayScale);
      // Keep popup state on before hiding wheel flags to avoid render races.
      setIsBoostedJackpotWinPopup(true);
      setIsWheelZoomOut(false);
    }, endAnimationDurationMs + 40);

    return () => {
      if (wheelEndTimeoutRef.current) {
        window.clearTimeout(wheelEndTimeoutRef.current);
        wheelEndTimeoutRef.current = null;
      }
      wheelIntroAnimation?.kill();
      wheelIntroAnimation = null;
      wheelSettleAnimation?.kill();
      wheelSettleAnimation = null;
      wheelEndAnimation?.kill();
      wheelEndAnimation = null;
      if (wheelSprite) gsap.killTweensOf(wheelSprite);
      if (wheelScale) gsap.killTweensOf(wheelScale);
      if (underlaySprite) gsap.killTweensOf(underlaySprite);
      if (underlayScale) gsap.killTweensOf(underlayScale);
    };
  }, [isWheelZoomOut]);

  return (
    <Container ref={wheelGroupRef}>
      <Sprite
        ref={wheelUnderlayRef}
        x={0}
        y={4}
        anchor={0.5}
        texture={sectorUnderlayTexture}
        width={sectorRadius * 2}
        height={sectorRadius * 2}
      />
      <Container x={0} y={0} ref={wheelThemeFrameRef} />
      {texture && <Sprite ref={wheelRef} x={0} y={0} anchor={0.5} texture={texture} />}
      {showWinOverlay && winOverlayTexture && (
        <Sprite
          x={0}
          y={0}
          anchor={0.5}
          texture={winOverlayTexture}
          width={sectorRadius * 2}
          height={sectorRadius * 2}
        />
      )}
      <Container y={-278} ref={wheelThemeArrowRef} />
      <Container x={0} y={0} ref={wheelThemeLogoRef} />
    </Container>
  );
});
